import {
  CheckCircle2,
  CircleSlash,
  Clock,
  type LucideIcon,
  XCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-12 — Shared status primitive.
 *
 * Visual language for the four invite-lifecycle states (UX Spec §"Status
 * Badge Visual Language"). Defined once here, reused verbatim by OKS-13 and
 * OKS-14 — single source of truth for icon + variant + i18n key per status.
 *
 * The mapping below is the contract; any change must update the OKS-12 UX
 * Spec table + the EN/FR YAML under `status.*`. Colour is decorative — every
 * status combines a distinct icon + visible text label so screen-reader
 * announcements + colour-blind users are never starved of signal.
 *
 * Status is **server-authoritative** (PRD Rule 8). FE never recomputes the
 * value from `expires_at` or any client-side clock; the server returns the
 * exact discriminant and we render it. The `data-state` attribute exposes
 * the prop value verbatim so the Vitest contract test (and the QA E2E
 * selector `admin-status-badge[data-status]`) can assert the rendering is
 * keyed off the prop, not derived.
 */
export type InvitationStatus =
  | "pending"
  | "expired"
  | "accepted"
  | "revoked"
  /**
   * OKS-13 — surfaces the agency Team list's interleaved member rows
   * alongside invitation rows. Members ship `status: "active"` from
   * `app/controllers/agency_team_controller.rb`; invitation rows ship
   * `pending` or `expired`. Visual reuses the `accepted` styling
   * (green check) — the i18n label is what differentiates ("Active" vs
   * "Accepted"). Single shared primitive keeps the team row simple.
   */
  | "active"

interface StatusVisual {
  variant: "default" | "secondary" | "destructive" | "outline"
  icon: LucideIcon
  /** Class overrides per UX Spec colour token (amber/muted/green/red-soft). */
  className: string
}

const STATUS_VISUAL: Record<InvitationStatus, StatusVisual> = {
  // Amber `Clock` — countdown feel; default `secondary` variant tinted amber.
  pending: {
    variant: "secondary",
    icon: Clock,
    className:
      "border-amber-300/60 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700/60",
  },
  // Muted `CircleSlash` — unambiguous "no longer valid" without alarming red.
  expired: {
    variant: "outline",
    icon: CircleSlash,
    className: "text-muted-foreground border-muted-foreground/30",
  },
  // Green `CheckCircle2` — success state.
  accepted: {
    variant: "default",
    icon: CheckCircle2,
    className:
      "border-transparent bg-emerald-600 text-white dark:bg-emerald-700",
  },
  // Red-soft `XCircle` — destructive but not as loud as a full `destructive`
  // variant; signals "manually killed" without dominating the layout.
  revoked: {
    variant: "outline",
    icon: XCircle,
    className:
      "border-rose-300/60 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800/60",
  },
  // OKS-13 active — same visual as accepted (green CheckCircle2). The
  // i18n label "Active" / "Active" differentiates from "Accepted" /
  // "Accepté" so SR users hear the role context, not the lifecycle one.
  active: {
    variant: "default",
    icon: CheckCircle2,
    className:
      "border-transparent bg-emerald-600 text-white dark:bg-emerald-700",
  },
}

/**
 * Resolve the `status.*` i18n label via literal `t()` arguments so the
 * inertia_i18n health scanner can statically prove every key is
 * reachable. Dynamic template strings (`t(\`status.${status}\`)`)
 * silently bypass the scanner. Same pattern as `roleLabel` /
 * `userTierLabel`.
 */
function statusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: InvitationStatus,
): string {
  switch (status) {
    case "pending":
      return t("status.pending")
    case "expired":
      return t("status.expired")
    case "accepted":
      return t("status.accepted")
    case "revoked":
      return t("status.revoked")
    case "active":
      return t("status.active")
  }
}

interface StatusBadgeProps {
  status: InvitationStatus
  /** Hide the visible label and keep only the icon. The `aria-label` still announces the status. */
  iconOnly?: boolean
  className?: string
  /** Override the locked `data-testid`. Defaults to `admin-status-badge` per QA contract. */
  testId?: string
}

/**
 * Renders an invite-lifecycle chip (icon + label + colour).
 *
 * Default `data-testid="admin-status-badge"` matches the QA-locked OKS-12
 * contract; OKS-13/14 callers override via `testId` (e.g.
 * `agency-status-badge`). Either way the `data-state` attribute is set to
 * the raw `status` prop — that's the contract assertion: the rendered chip
 * is keyed off `props.status`, not derived from `expires_at`.
 */
export function StatusBadge({
  status,
  iconOnly = false,
  className,
  testId = "admin-status-badge",
}: StatusBadgeProps) {
  const { t } = useTranslation()
  const visual = STATUS_VISUAL[status]
  const Icon = visual.icon
  const label = statusLabel(t, status)

  return (
    <Badge
      variant={visual.variant}
      role="status"
      aria-label={label}
      data-testid={testId}
      data-state={status}
      className={cn("gap-1.5", visual.className, className)}
    >
      <Icon aria-hidden="true" />
      {!iconOnly && <span>{label}</span>}
    </Badge>
  )
}
